import { supabase } from "../lib/supabase";

export type NovaReservationSubmission = {
  name: string;
  email: string;
  date: string;
  time: string;
  guests: string;
  note: string;
};

export type NovaServiceType =
  | "birthday_cake"
  | "wedding_cake"
  | "celebration_cake"
  | "outside_catering"
  | "corporate_lunches"
  | "meeting_snacks"
  | "private_functions"
  | "birthday_celebration"
  | "wedding_reception"
  | "corporate_event"
  | "other";

export type NovaServiceEnquirySubmission = {
  name: string;
  phone: string;
  email: string;
  serviceType: NovaServiceType;
  eventDate: string;
  guestCount: string;
  budget: string;
  location: string;
  preferredTime: string;
  requirements: string;
  dietaryRequirements: string;
};

export async function submitNovaReservation(
  submission: NovaReservationSubmission,
): Promise<{
  ok: boolean;
  id: string | null;
  error: string | null;
}> {
  const { data, error } = await supabase.rpc(
    "submit_nova_reservation",
    {
      p_name: submission.name.trim(),
      p_email: submission.email.trim(),
      p_date: submission.date,
      p_time: submission.time,
      p_guests: submission.guests,
      p_note:
        submission.note.trim() || null,
    },
  );

  if (error) {
    console.error(
      "[Nova] reservation submission failed",
      error,
    );

    return {
      ok: false,
      id: null,
      error:
        error.message ||
        "We could not send the reservation request.",
    };
  }

  return {
    ok: true,
    id:
      typeof data === "string"
        ? data
        : null,
    error: null,
  };
}

export async function submitNovaServiceEnquiry(
  submission: NovaServiceEnquirySubmission,
): Promise<{
  ok: boolean;
  id: string | null;
  error: string | null;
}> {
  const parsedGuestCount =
    submission.guestCount.trim() === ""
      ? null
      : Number.parseInt(
          submission.guestCount,
          10,
        );

  const { data, error } = await supabase.rpc(
    "submit_nova_service_enquiry",
    {
      p_name: submission.name.trim(),
      p_phone: submission.phone.trim(),
      p_email:
        submission.email.trim() || null,
      p_service_type:
        submission.serviceType,
      p_event_date:
        submission.eventDate || null,
      p_guest_count:
        Number.isFinite(parsedGuestCount)
          ? parsedGuestCount
          : null,
      p_budget:
        submission.budget.trim() || null,
      p_location:
        submission.location.trim() || null,
      p_preferred_time:
        submission.preferredTime.trim() ||
        null,
      p_requirements:
        submission.requirements.trim() ||
        null,
      p_dietary_requirements:
        submission.dietaryRequirements.trim() ||
        null,
    },
  );

  if (error) {
    console.error(
      "[Nova] service enquiry submission failed",
      error,
    );

    return {
      ok: false,
      id: null,
      error:
        error.message ||
        "We could not send your enquiry.",
    };
  }

  return {
    ok: true,
    id:
      typeof data === "string"
        ? data
        : null,
    error: null,
  };
}

export type NovaGuestFeedbackSubmission = {
  guestName: string;
  email: string;
  phone: string;
  visitDate: string;
  overallRating: number;
  foodRating: number | null;
  serviceRating: number | null;
  atmosphereRating: number | null;
  highlights: string;
  improvements: string;
  additionalComments: string;
  wouldReturn: boolean | null;
  permissionToContact: boolean;
};

export async function submitNovaGuestFeedback(
  submission: NovaGuestFeedbackSubmission,
): Promise<{
  ok: boolean;
  id: string | null;
  error: string | null;
}> {
  const { data, error } = await supabase.rpc(
    "submit_nova_guest_feedback",
    {
      p_guest_name:
        submission.guestName.trim() || null,
      p_email:
        submission.email.trim() || null,
      p_phone:
        submission.phone.trim() || null,
      p_visit_date:
        submission.visitDate || null,
      p_overall_rating:
        submission.overallRating,
      p_food_rating:
        submission.foodRating,
      p_service_rating:
        submission.serviceRating,
      p_atmosphere_rating:
        submission.atmosphereRating,
      p_highlights:
        submission.highlights.trim() ||
        null,
      p_improvements:
        submission.improvements.trim() ||
        null,
      p_additional_comments:
        submission.additionalComments.trim() ||
        null,
      p_would_return:
        submission.wouldReturn,
      p_permission_to_contact:
        submission.permissionToContact,
    },
  );

  if (error) {
    console.error(
      "[Nova] guest feedback submission failed",
      error,
    );

    return {
      ok: false,
      id: null,
      error:
        error.message ||
        "We could not send your feedback.",
    };
  }

  return {
    ok: true,
    id:
      typeof data === "string"
        ? data
        : null,
    error: null,
  };
}
